/* eslint-disable eqeqeq */
import type { AccessoryFormApi } from './useAccessoryForm';

/**
 * Phase 6e2b-1 — THE shared `<MountingDistances>` block (§52.1: the repeated
 * 6-distance grammar that dominates every accessory tmpl's position_field
 * census). One parameterized component covering the per-type variants found
 * in the 10 tmpls:
 *  - 'light':   roof + 4 walls carry Auto-Position checkboxes (+readonly
 *               gating); floor rows are plain inputs;
 *  - 'clone':   (jbox/disconnect/enclosure/adp_gage/vfd) 4 walls carry
 *               Auto-Position; roof+floor rows readonly-gate on default_pos
 *               but have NO checkbox;
 *  - 'dogHouse': wall_left + wall_right only, no Auto-Position, no gating.
 *
 * Per-plane row map (verbatim from the tmpls):
 *  roof/floor:        y ↔ position_Wy (distance_to_right_wall)   "To Wall Left | Right"
 *                     x ↔ position_Lx (distance_to_downstream_wall) "To Wall Ups | Downs"
 *  wall_downstream:   y ↔ position_Hy (distance_to_roof)         "To Floor | Roof"
 *                     x ↔ position_Wx (distance_to_right_wall)   "To Wall Left | Right"
 *  wall_left/right:   y ↔ position_Hy                            "To Floor | Roof"
 *                     x ↔ position_Lx                            "To Wall Ups | Downs"
 *  wall_upstream:     y ↔ position_Hy                            "To Floor | Roof"
 *                     x ↔ position_Wx                            "To Wall Left | Right"
 *
 * Display: position x/y + distance_to_* render toFixed(2) (§49.6:798-802).
 */

export type MountingVariant = 'light' | 'clone' | 'dogHouse';

const fix2 = (v: unknown): string => {
  if (v == null || v === '') return '';
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(2) : String(v);
};

interface PlaneRowSpec {
  axis: 'x' | 'y';
  compName: string;
  compField: string;
  label: [string, string];
}

const PLANE_ROWS: Record<string, [PlaneRowSpec, PlaneRowSpec]> = {
  roof: [
    { axis: 'y', compName: 'position_Wy', compField: 'distance_to_right_wall', label: ['To Wall Left', 'Right'] },
    { axis: 'x', compName: 'position_Lx', compField: 'distance_to_downstream_wall', label: ['To Wall Ups', 'Downs'] },
  ],
  floor: [
    { axis: 'y', compName: 'position_Wy', compField: 'distance_to_right_wall', label: ['To Wall Left', 'Right'] },
    { axis: 'x', compName: 'position_Lx', compField: 'distance_to_downstream_wall', label: ['To Wall Ups', 'Downs'] },
  ],
  wall_downstream: [
    { axis: 'y', compName: 'position_Hy', compField: 'distance_to_roof', label: ['To Floor', 'Roof'] },
    { axis: 'x', compName: 'position_Wx', compField: 'distance_to_right_wall', label: ['To Wall Left', 'Right'] },
  ],
  wall_left: [
    { axis: 'y', compName: 'position_Hy', compField: 'distance_to_roof', label: ['To Floor', 'Roof'] },
    { axis: 'x', compName: 'position_Lx', compField: 'distance_to_downstream_wall', label: ['To Wall Ups', 'Downs'] },
  ],
  wall_right: [
    { axis: 'y', compName: 'position_Hy', compField: 'distance_to_roof', label: ['To Floor', 'Roof'] },
    { axis: 'x', compName: 'position_Lx', compField: 'distance_to_downstream_wall', label: ['To Wall Ups', 'Downs'] },
  ],
  wall_upstream: [
    { axis: 'y', compName: 'position_Hy', compField: 'distance_to_roof', label: ['To Floor', 'Roof'] },
    { axis: 'x', compName: 'position_Wx', compField: 'distance_to_right_wall', label: ['To Wall Left', 'Right'] },
  ],
};

const DOG_HOUSE_PLANES = new Set(['wall_left', 'wall_right']);

const PositionInput = ({
  api,
  name,
  value,
  readOnlyGated,
}: {
  api: AccessoryFormApi;
  name: string;
  value: unknown;
  readOnlyGated: boolean;
}) => (
  <input
    type="text"
    data-formatter_type="decimal"
    autoComplete="off"
    data-min="0"
    name={name}
    className="position_field medium-input"
    readOnly={readOnlyGated}
    defaultValue={fix2(value)}
    key={`${name}_${fix2(value)}`}
    onBlur={(e) => {
      if (readOnlyGated) return;
      if (e.currentTarget.value !== fix2(value)) {
        api.commitPositionField(name, e.currentTarget.value);
      }
    }}
    onKeyDown={(e) => {
      if (e.key === 'Enter' && !readOnlyGated) {
        const v = (e.target as HTMLInputElement).value;
        if (v !== fix2(value)) api.commitPositionField(name, v);
      }
    }}
  />
);

const PlaneRow = ({
  api,
  spec,
  variant,
  planeValue,
}: {
  api: AccessoryFormApi;
  spec: PlaneRowSpec;
  variant: MountingVariant;
  planeValue: string;
}) => {
  const pos = api.position;
  const model = api.model;
  const defaultPos = !!pos?.get(spec.axis === 'y' ? 'default_pos_y' : 'default_pos_x');
  const isFloor = planeValue === 'floor';
  const isRoof = planeValue === 'roof';
  // Auto-Position checkbox presence per tmpl census:
  const hasCheckbox =
    variant === 'light' ? !isFloor : variant === 'clone' ? !isFloor && !isRoof : false;
  // readonly gating per tmpl census (light floor rows are ungated; dog_house ungated):
  const gated =
    variant === 'dogHouse' ? false : variant === 'light' && isFloor ? false : defaultPos;
  const checkboxName = spec.axis === 'y' ? 'default_pos_y' : 'default_pos_x';
  return (
    <li>
      <div className="accessory-mounting-floor">
        {/* §JSX-WS — the two `{' '}`s are load-bearing, exactly as in
            complexMounting.tsx's two-input row. `.unit` is inline-block
            (`>.unit { width: 40px; display: inline-block }`) and both boxes are
            inline `.medium-input`s, so the NEWLINES the tmpls leave between
            `</span>`, the editable input and its readonly companion render as
            two real spaces (dog_house form.tmpl:205-216, matched by
            light/jbox/vfd/adp_gage/disconnect/enclosure). No space before the
            floated `.for-center-position` checkbox or the floated `.label`. */}
        <span className="unit">in</span>{' '}
        <PositionInput
          api={api}
          name={`position_${spec.axis}`}
          value={pos?.get(spec.axis)}
          readOnlyGated={gated}
        />{' '}
        <input
          type="text"
          data-formatter_type="decimal"
          autoComplete="off"
          data-min="0"
          name={spec.compName}
          className="position_field medium-input"
          value={fix2(model?.get(spec.compField))}
          readOnly
          tabIndex={-1}
          onChange={() => undefined}
        />
        {hasCheckbox && (
          <label className="for-center-position">
            {/* §JSX-WS — same load-bearing space as complexMounting.tsx: the
                light/jbox/disconnect/enclosure/adp_gage/vfd form.tmpls all put
                a NEWLINE between the checkbox and the caption. */}
            <input
              type="checkbox"
              name={checkboxName}
              className="position_field"
              checked={defaultPos}
              onChange={(e) => api.commitPositionCheckbox(checkboxName, e.target.checked)}
            />{' '}
            <span>Auto-Position</span>
          </label>
        )}
        <span className="label">
          <span>{spec.label[0]}</span> | <span>{spec.label[1]}</span>
        </span>
      </div>
    </li>
  );
};

/**
 * The full mounting subtab li: subtab input/label + plane select row (with
 * location_btn) + the per-plane distance rows + optional trailing children
 * (light's orientation row).
 */
const MountingSection = ({
  api,
  type,
  cid,
  variant,
  sublistDisabled,
  sublistExtraClass,
  locationBtnDisabled,
  liClass = 'mounting-section-wrapper',
  planeLabelRequired = true,
  leadingChildren,
  children,
}: {
  api: AccessoryFormApi;
  type: string;
  cid: number | string;
  variant: MountingVariant;
  /** legacy `non-clickable` gating (master null / disableMounting false). */
  sublistDisabled?: boolean;
  sublistExtraClass?: string;
  locationBtnDisabled?: boolean;
  liClass?: string;
  planeLabelRequired?: boolean;
  /** rows rendered BEFORE the plane row (vfd: In-Enclosure W×H row). */
  leadingChildren?: React.ReactNode;
  children?: React.ReactNode;
}) => {
  const open = !!api.subtabs.mounting;
  const pos = api.position;
  const planeId = pos ? Number(pos.get('mounting_plane_id')) : null;
  const plane = api.currentPlane;
  const planeValue = String(plane?.value ?? '');
  const rows =
    planeId && PLANE_ROWS[planeValue] && (variant !== 'dogHouse' || DOG_HOUSE_PLANES.has(planeValue))
      ? PLANE_ROWS[planeValue]
      : null;
  const id = `${type}_${cid}_mounting`;
  return (
    <li className={liClass}>
      <input
        id={id}
        data-subtab="mounting"
        type="checkbox"
        checked={open}
        onChange={(e) => api.toggleSubtab('mounting', e.target.checked)}
      />
      <label htmlFor={id}>
        Mounting
        <i className="icon-add" />
      </label>
      <ul
        className={`sublist${sublistDisabled ? ' non-clickable' : ''}${
          sublistExtraClass ? ` ${sublistExtraClass}` : ''
        }`}
      >
        {leadingChildren}
        <li>
          <div className="mounting-plane">
            <div
              className={`checkboxes-group mounting-btn${
                planeId != null && planeId > 6 ? ' non-clickable' : ''
              }`}
            >
              <button
                type="button"
                className={`${locationBtnDisabled ? 'non-clickable ' : ''}icon-mounting-1 btn-location`}
                name="location_btn"
                onClick={() => {
                  if (!locationBtnDisabled) api.openMountingPopup();
                }}
              />
            </div>{' '}
            {/* §JSX-WS — load-bearing spaces. Every mounting tmpl
                (disconnect/jbox/light/vfd/enclosure/dog_house/adp_gage
                form.tmpl) puts the `.mounting-btn` div, the `.selection` span
                and the plane `<select>` on their own lines. `.selection` is
                `position:absolute` (ahu/styles.css:3083) so it is out of flow,
                but the surrounding newlines still collapse to ONE real ~4px
                space between the 66px button and the select — both boxes are
                inline-block. Without it the select (and the selection text
                that sits on top of it) renders 4px left of legacy. */}
            <span className="selection">
              {plane ? <span>{String(plane.name ?? '')}</span> : null}
            </span>{' '}
            <select
              name="mounting_plane_id"
              className="position_field"
              required
              value={planeId != null && Number.isFinite(planeId) && planeId ? String(planeId) : 'None'}
              disabled={locationBtnDisabled}
              onChange={(e) => {
                const opt = e.currentTarget.options[e.currentTarget.selectedIndex];
                api.commitMountingPlane(e.currentTarget.value, opt?.dataset.position);
              }}
            >
              <option value="None" className="hide">
                - -
              </option>
              {api.mountingPlanes.map((p) => (
                <option key={String(p.id)} value={String(p.id)} data-position="on_cabinet">
                  {String(p.name ?? '')}
                </option>
              ))}
            </select>
            <span className={`label${planeLabelRequired ? ' required' : ''}`}>
              Mounting | Plane
            </span>
          </div>
        </li>
        {rows && (
          <>
            <PlaneRow api={api} spec={rows[0]} variant={variant} planeValue={planeValue} />
            <PlaneRow api={api} spec={rows[1]} variant={variant} planeValue={planeValue} />
          </>
        )}
        {children}
      </ul>
    </li>
  );
};

export default MountingSection;
